import { useContext } from 'react'
import { ApplicationContext } from '@contexts/ApplicationContext'

import FeatureAccessProvider from './useFeatureConfig.AccessProvider'
import FeatureOptionsProvider from './useFeatureConfig.OptionsProvider'

/**
 * useFeatureConfig provides a way for a component to get one or many feature
 * objects. Feature objects provide a consistent interface: `isEnabled` and
 * `getOptions` to check whether it's enabled and get it's additional
 * configuration options.
 *
 * When retrieving multiple features, the feature objects are order dependent
 * and will always return them in the same order in which the feature names
 * were passed in. The features objects can also be destructured and aliased to
 * locally appropriate names:
 *
 * ```js
 * const [
 *   createScenarioFeature,
 *   {
 *     isEnabled: exportScenarioProjectionsEnabled,
 *     getOptions: getExportScenarioProjectionsOptions
 *   }
 * ] = useFeatureConfig(
 *   FeatureNames.SCENARIO__CREATE, FeatureNames.SCENARIO__EXPORT_PROJECTIONS
 * )
 * ```
 */
export default function useFeatureConfig(...featureNames) {
  const { featureConfig, featureToggles, permissions } = useContext(ApplicationContext)

  const hasPermission = requiredPermission => permissions.includes(requiredPermission)
  const hasPermissions = (...requiredPermissions) => (
    requiredPermissions.reduce((hasSoFar, current) => hasSoFar && hasPermission(current), true)
  )
  const hasFeatureToggle = (featureToggle) => (
    featureToggles[featureToggle] !== undefined &&
      featureToggles[featureToggle] !== null
  )
  const getFeatureOption = (name, option) => {
    const featureOptions = featureConfig[name]
    if (featureOptions === undefined) {
      return
    }

    if (option) {
      return featureOptions[option]
    } else {
      return featureOptions
    }
  }

  const accessProvider = FeatureAccessProvider(hasPermissions, hasFeatureToggle)
  const isEnabled = (featureName, args) => accessProvider.isEnabled(featureName, args)

  const optionsProvider = FeatureOptionsProvider(getFeatureOption)
  const getOptions = (featureName, args) => optionsProvider.getOptions(featureName, args)

  const featureConfigurationProvider = getFeatureConfigurationProvider(isEnabled, getOptions)

  if (featureNames.length === 1) {
    return featureConfigurationProvider(featureNames[0])
  } else {
    return featureNames.map(featureName => featureConfigurationProvider(featureName))
  }
}

function getFeatureConfigurationProvider(isEnabled, getOptions) {
  return featureName => ({
    isEnabled: (args) => isEnabled(featureName, args),
    getOptions: (args) => getOptions(featureName, args)
  })
}

export {
  default as FeatureNames
} from './useFeatureConfig.constants'
